import { UseFormSetError } from 'react-hook-form';
import {
  findDependentOnSaleEventTableColumnIds,
  ON_SALE_EVENT_FORMULA_FIELDS_TO_CID,
  validateOnSaleEventFormula,
} from 'src/navigations/Routes/OnSaleDiscovery/OnSaleDiscoveryCustomColumnUtils';
import {
  findDependentEventsTableColumnIds,
  validateFormula as validateFormulaEvents,
} from 'src/utils/columns/events/eventsCustomColumnUtils';
import {
  CustomEventsColumn,
  CustomOnSaleEventsColumn,
} from 'src/utils/columns/events/eventsCustomColumnUtils.types';
import {
  findDependentListingTableColumnIds as findDependentListingTableColumnIdsListing,
  validateFormula as validateFormulaListing,
} from 'src/utils/columns/inventory/inventoryCustomColumnUtils';
import { CustomListingColumn } from 'src/utils/columns/inventory/inventoryCustomColumnUtils.types';
import {
  findDependentListingTableColumnIds as findDependentListingTableColumnIdsListingReport,
  validateFormula as validateFormulaListingReport,
} from 'src/utils/columns/inventory/inventoryReportCustomColumnUtils';
import { CustomMarketplacePaymentsColumn } from 'src/utils/columns/payments/paymentsCustomColumnUtils.types';
import {
  findDependentPurchaseTableColumnIds,
  validateFormula as validateFormulaPurchases,
} from 'src/utils/columns/purchases/purchasesCustomColumnUtils';
import { CustomPurchasesColumn } from 'src/utils/columns/purchases/purchasesCustomColumnUtils.types';
import {
  findDependentSaleTableColumnIds,
  validateFormula as validateFormulaSales,
} from 'src/utils/columns/sales/salesCustomColumnUtils';
import { CustomSalesColumn } from 'src/utils/columns/sales/salesCustomColumnUtils.types';
import {
  EVENTS_TABLE_COLUMN_ID_TO_CONTENT_ID,
  LISTING_REPORT_TABLE_COLUMN_ID_TO_CONTENT_ID,
  LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID,
  LISTING_TABLE_FLATTENED_COLUMN_ID_TO_CONTENT_ID,
  ON_SALE_EVENT_TABLE_COLUMN_ID_CONTENT_ID,
  PAYMENTS_TABLE_COLUMN_ID_TO_CONTENT_ID,
  PURCHASE_TABLE_COLUMN_ID_TO_CONTENT_ID,
  PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID,
  SALE_TABLE_COLUMN_ID_TO_CONTENT_ID,
  SALE_TABLE_FLATTENED_COLUMN_ID_TO_CONTENT_ID,
  SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID,
} from 'src/utils/constants/contentIdMaps';
import {
  EVENTS_FORMULA_FIELDS_TO_CID,
  LISTING_FORMULA_FIELDS_TO_CID,
  LISTING_REPORT_FORMULA_FIELDS_TO_CID,
  PURCHASE_FORMULA_FIELDS_TO_CID,
  SALE_FORMULA_FIELDS_TO_CID,
} from 'src/utils/constants/contentIdMaps';
import { SectionType } from 'src/utils/types/sectionType';
import { ActionOutboxEntityType, Tag } from 'src/WebApiController';

import { CustomColumnType } from './TableCustomColumnModal.types';

export const SECTION_TYPE_TO_PRESET_COLUMNS = {
  [SectionType.Events]: EVENTS_TABLE_COLUMN_ID_TO_CONTENT_ID,
  [SectionType.Listings]: LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID,
  [SectionType.ListingsFlattened]: {
    ...LISTING_TABLE_FLATTENED_COLUMN_ID_TO_CONTENT_ID,
    ...LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID,
  },
  [SectionType.ListingsReport]: LISTING_REPORT_TABLE_COLUMN_ID_TO_CONTENT_ID,
  [SectionType.InventorySideTable]: LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID,
  [SectionType.Sales]: {
    ...SALE_TABLE_COLUMN_ID_TO_CONTENT_ID,
    ...SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID,
  },
  [SectionType.SalesFlattened]: {
    ...SALE_TABLE_FLATTENED_COLUMN_ID_TO_CONTENT_ID,
    ...SALE_TABLE_COLUMN_ID_TO_CONTENT_ID,
  },
  [SectionType.SalesReport]: SALE_TABLE_COLUMN_ID_TO_CONTENT_ID,
  [SectionType.SalesSideTable]: SALE_TABLE_COLUMN_ID_TO_CONTENT_ID,
  [SectionType.Purchases]: {
    ...PURCHASE_TABLE_COLUMN_ID_TO_CONTENT_ID,
    ...PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID,
  },
  [SectionType.PurchaseEvent]: {
    ...PURCHASE_TABLE_COLUMN_ID_TO_CONTENT_ID,
    ...PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID,
  },
  [SectionType.PurchaseSideTable]: {
    ...PURCHASE_TABLE_COLUMN_ID_TO_CONTENT_ID,
    ...PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID,
  },
  [SectionType.MarketplacePaymentsTable]:
    PAYMENTS_TABLE_COLUMN_ID_TO_CONTENT_ID,
  [SectionType.OnSaleEvents]: ON_SALE_EVENT_TABLE_COLUMN_ID_CONTENT_ID,
  [SectionType.RecentSalesTable]: SALE_TABLE_COLUMN_ID_TO_CONTENT_ID,
};

export const sectionTypeToActionOutboxEntityType = (
  sectionType: SectionType
) => {
  switch (sectionType) {
    case SectionType.Events:
      return ActionOutboxEntityType.SellerEvent;
    case SectionType.Listings:
    case SectionType.ListingsFlattened:
    case SectionType.ListingsReport:
    case SectionType.InventorySideTable:
      return ActionOutboxEntityType.Listing;
    case SectionType.Sales:
    case SectionType.SalesFlattened:
    case SectionType.SalesReport:
    case SectionType.SalesSideTable:
      return ActionOutboxEntityType.Sale;
    case SectionType.Purchases:
    case SectionType.PurchaseEvent:
    case SectionType.PurchaseSideTable:
      return ActionOutboxEntityType.Purchase;
    default:
      return undefined;
  }
};

export const getSectionTypeColumnFns = (sectionType: SectionType) => {
  switch (sectionType) {
    case SectionType.Listings:
      return {
        findDependentListingTableColumnIds:
          findDependentListingTableColumnIdsListing,
        validateFormula: validateFormulaListing,
        fieldOptionsPreset: LISTING_FORMULA_FIELDS_TO_CID,
      };
    case SectionType.ListingsReport:
      return {
        findDependentListingTableColumnIds:
          findDependentListingTableColumnIdsListingReport,
        validateFormula: validateFormulaListingReport,
        fieldOptionsPreset: LISTING_REPORT_FORMULA_FIELDS_TO_CID,
      };
    case SectionType.Sales:
      return {
        findDependentListingTableColumnIds: findDependentSaleTableColumnIds,
        validateFormula: validateFormulaSales,
        fieldOptionsPreset: SALE_FORMULA_FIELDS_TO_CID,
      };
    case SectionType.SalesReport:
      return {
        findDependentListingTableColumnIds: findDependentSaleTableColumnIds,
        validateFormula: validateFormulaSales,
        fieldOptionsPreset: SALE_FORMULA_FIELDS_TO_CID,
      };
    case SectionType.OnSaleEvents:
      return {
        findDependentListingTableColumnIds:
          findDependentOnSaleEventTableColumnIds,
        validateFormula: validateOnSaleEventFormula,
        fieldOptionsPreset: ON_SALE_EVENT_FORMULA_FIELDS_TO_CID,
      };
    case SectionType.Purchases:
    case SectionType.PurchaseEvent:
    case SectionType.PurchaseSideTable:
      return {
        findDependentListingTableColumnIds: findDependentPurchaseTableColumnIds,
        validateFormula: validateFormulaPurchases,
        fieldOptionsPreset: PURCHASE_FORMULA_FIELDS_TO_CID,
      };
    default:
      return {
        findDependentListingTableColumnIds: findDependentEventsTableColumnIds,
        validateFormula: validateFormulaEvents,
        fieldOptionsPreset: EVENTS_FORMULA_FIELDS_TO_CID,
      };
  }
};

export const getColumnIdFromFormulaFieldId = (
  sectionType: SectionType,
  field: string
) => {
  switch (sectionType) {
    case SectionType.Listings:
      return LISTING_FORMULA_FIELDS_TO_CID[
        field as keyof typeof LISTING_FORMULA_FIELDS_TO_CID
      ];
    case SectionType.ListingsReport:
      return LISTING_REPORT_FORMULA_FIELDS_TO_CID[
        field as keyof typeof LISTING_REPORT_FORMULA_FIELDS_TO_CID
      ];
    case SectionType.Sales:
    case SectionType.SalesReport:
      return SALE_FORMULA_FIELDS_TO_CID[
        field as keyof typeof SALE_FORMULA_FIELDS_TO_CID
      ];
    case SectionType.OnSaleEvents:
      return ON_SALE_EVENT_FORMULA_FIELDS_TO_CID[
        field as keyof typeof ON_SALE_EVENT_FORMULA_FIELDS_TO_CID
      ];
    case SectionType.Purchases:
    case SectionType.PurchaseEvent:
    case SectionType.PurchaseSideTable:
      return PURCHASE_FORMULA_FIELDS_TO_CID[
        field as keyof typeof PURCHASE_FORMULA_FIELDS_TO_CID
      ];
    default:
      return EVENTS_FORMULA_FIELDS_TO_CID[
        field as keyof typeof EVENTS_FORMULA_FIELDS_TO_CID
      ];
  }
};

export function validateNewCustomColumn<
  T extends
    | CustomListingColumn
    | CustomSalesColumn
    | CustomEventsColumn
    | CustomPurchasesColumn
    | CustomMarketplacePaymentsColumn
    | CustomOnSaleEventsColumn,
>(
  name: string,
  formula: string,
  allPresetColumnNames: string[],
  customColumns: T[],
  columnToEditId: string | undefined,
  tagsMetadataNumeric: Tag[] | undefined,
  setError: UseFormSetError<CustomColumnType>,
  columnWithSameNameExistsMsg: string,
  invalidFormulaMsg: string,
  requiredMsg: string,
  validateFormula: (
    formula: string,
    tagsForEntityType: Tag[] | undefined
  ) => boolean
) {
  let hasError = false;

  // check against not only custom column but predefined columns
  const allColumnNames = [
    ...allPresetColumnNames,
    ...customColumns.map((c) => c.name),
  ];
  if (
    allColumnNames.some(
      (n) => n.toLocaleLowerCase() === name.toLocaleLowerCase()
    )
  ) {
    if (
      !columnToEditId ||
      (columnToEditId &&
        customColumns.find((c) => c.id === columnToEditId)?.name !== name)
    ) {
      setError(
        'name',
        { message: columnWithSameNameExistsMsg },
        { shouldFocus: true }
      );
      hasError = true;
    }
  }

  if (!validateFormula(formula, tagsMetadataNumeric)) {
    setError('formula', { message: invalidFormulaMsg }, { shouldFocus: true });
    hasError = true;
  }
  // check empty
  if (!name) {
    setError('name', { message: requiredMsg }, { shouldFocus: true });
    hasError = true;
  }

  if (!formula) {
    setError('formula', { message: requiredMsg }, { shouldFocus: true });
    hasError = true;
  }

  return !hasError;
}
